import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { UserService } from 'src/app/admin/services/user.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import {
  CurrentUserInfo,
  CurrentUserService,
} from 'src/app/shared/services/current-user.service';
import { CreateUserDTO } from '../../model/user';
import { ApiError } from 'src/app/trip-calculator/models/api-response';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class UserFormService {
  fb = inject(FormBuilder);
  notificationService = inject(NotificationService);
  translocoService = inject(TranslocoService);
  currentUserService = inject(CurrentUserService);
  userService = inject(UserService);
  user?: CurrentUserInfo;
  private initialLanguage = '';

  userForm = this.fb.group({
    firstName: [''],
    lastName: [''],
    email: ['', Validators.email],
    phone: [''],
    role: [''],
    preferredLanguage: [''],
    marketingOptOut: [false],
  });

  // Signals for tracking dirty state
  private userInfoDirtySignal = signal(false);
  private languageDirtySignal = signal(false);
  private communicationsDirtySignal = signal(false);

  // Loading signals for each section
  isLoadingUserInfo = signal(false);
  isLoadingLanguage = signal(false);
  isLoadingCommunications = signal(false);

  // Computed for external access
  userInfoDirty = computed(() => this.userInfoDirtySignal());
  languageDirty = computed(() => this.languageDirtySignal());
  communicationsDirty = computed(() => this.communicationsDirtySignal());

  // Store initial value for comparison
  private initialMarketingOptOut = false;

  constructor() {
    effect(() => {
      const user = this.currentUserService.currentUser();
      if (user && user.id !== this.user?.id) {
        this.user = user;
        this.populateForm();
      }
    });

    // Track form changes
    this.userForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateDirtyState());
  }

  private updateDirtyState() {
    if (!this.user) return;

    const formValues = this.userForm.value;

    // Check if user info fields changed
    const userInfoChanged =
      formValues.firstName !== this.user.firstName ||
      formValues.lastName !== this.user.lastName ||
      formValues.email !== this.user.email ||
      formValues.phone !== this.user.phone;

    this.userInfoDirtySignal.set(userInfoChanged);

    // Check if language changed
    const languageChanged =
      formValues.preferredLanguage !== this.initialLanguage;
    this.languageDirtySignal.set(languageChanged);

    // Check if marketing opt-out changed
    const communicationsChanged = formValues.marketingOptOut !== this.initialMarketingOptOut;
    this.communicationsDirtySignal.set(communicationsChanged);
  }

  populateForm() {
    const user = this.user;
    if (user) {
      this.initialLanguage = this.translocoService.getActiveLang();
      this.initialMarketingOptOut = user.marketingOptOut ?? false;
      this.userForm.patchValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        role: user.role,
        preferredLanguage: this.initialLanguage,
        marketingOptOut: this.initialMarketingOptOut,
      });
      // Reset dirty state after populating
      this.userInfoDirtySignal.set(false);
      this.languageDirtySignal.set(false);
      this.communicationsDirtySignal.set(false);
    }
  }

  submitUserInfo() {
    const formValues = this.userForm.value;
    if (!this.user?.id) return;

    this.isLoadingUserInfo.set(true);

    const userDto: CreateUserDTO = {
      ...this.user,
      firstName: formValues.firstName ?? '',
      lastName: formValues.lastName ?? '',
      email: formValues.email ?? '',
      phone: formValues.phone ?? '',
      role: formValues.role ?? '',
      preferredLanguage: this.translocoService.getActiveLang(),
    };

    this.userService.update(this.user.id, userDto).subscribe({
      next: () => {
        this.notificationService.success(
          this.translocoService.translate('accountMenu.userUpdated'),
        );
        this.currentUserService.refreshCurrentUser();
        this.isLoadingUserInfo.set(false);
        this.userInfoDirtySignal.set(false);
      },
      error: (err: ApiError) => {
        this.notificationService.error(err.error.message);
        this.isLoadingUserInfo.set(false);
      },
    });
  }

  submitLanguage() {
    const formValues = this.userForm.value;
    if (!formValues.preferredLanguage) return;

    this.isLoadingLanguage.set(true);

    // Update language locally
    this.translocoService.setActiveLang(formValues.preferredLanguage);
    localStorage.setItem('selectedLanguage', formValues.preferredLanguage);

    // Also update user preference on server if user exists
    if (this.user?.id) {
      const userDto: CreateUserDTO = {
        ...this.user,
        preferredLanguage: formValues.preferredLanguage,
      };

      this.userService.update(this.user.id, userDto).subscribe({
        next: () => {
          this.notificationService.success(
            this.translocoService.translate('accountMenu.languageUpdated'),
          );
          this.initialLanguage = formValues.preferredLanguage!;
          this.languageDirtySignal.set(false);
          this.isLoadingLanguage.set(false);
        },
        error: (err: ApiError) => {
          this.notificationService.error(err.error.message);
          this.isLoadingLanguage.set(false);
        },
      });
    } else {
      this.notificationService.success(
        this.translocoService.translate('accountMenu.languageUpdated'),
      );
      this.initialLanguage = formValues.preferredLanguage;
      this.languageDirtySignal.set(false);
      this.isLoadingLanguage.set(false);
    }
  }

  submitCommunications() {
    const formValues = this.userForm.value;
    if (!this.user?.id) return;

    this.isLoadingCommunications.set(true);

    const userDto: CreateUserDTO = {
      ...this.user,
      marketingOptOut: formValues.marketingOptOut ?? false,
    };

    this.userService.update(this.user.id, userDto).subscribe({
      next: () => {
        this.notificationService.success(
          this.translocoService.translate('accountMenu.communicationsUpdated'),
        );
        this.currentUserService.refreshCurrentUser();
        this.initialMarketingOptOut = formValues.marketingOptOut ?? false;
        this.communicationsDirtySignal.set(false);
        this.isLoadingCommunications.set(false);
      },
      error: (err: ApiError) => {
        this.notificationService.error(err.error.message);
        this.isLoadingCommunications.set(false);
      },
    });
  }

  reset() {
    this.userForm.reset();
    this.populateForm();
  }
}
